import React, { memo } from 'react';
import { Button, Dropdown, Input } from 'antd';
import { ClockCircleOutlined, MoreOutlined, PlusOutlined, TeamOutlined } from '@ant-design/icons';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import ChecklistItemRow from './ChecklistItemRow';
import confirmDanger from './confirmDanger';
import DescriptionEditor from './DescriptionEditor';
import DragHandle from './DragHandle';
import SectionSchedule from './SectionSchedule';
import SettingChip from './SettingChip';
import WhoSeesIt from './WhoSeesIt';
import { groupsLabel } from './checklistDraft';
import { SCHEDULE, scheduleLabel, scheduleMode } from './checklistSchedule';
import { sectionDeleteWarning } from './checklistWarnings';

const ChecklistSectionCard = props => {
	const { section, index, event, groups, counts, savedItemIDs, focusID } = props;
	const { onChange, onDelete, onPickLink, onChangeItem, onDeleteItem, onAddItem } = props;
	const changeSection = patch => onChange(section.id, patch);
	const who = groupsLabel(groups, section.group_ids);
	const when = scheduleLabel(event, section);

	const confirmDelete = () => {
		const warning = sectionDeleteWarning(section, counts, savedItemIDs);
		if (!warning) return onDelete(section.id);
		return confirmDanger(warning, 'Delete', () => onDelete(section.id));
	};

	return (
		<Draggable draggableId={section.id} index={index}>
			{provided => (
				<section
					ref={provided.innerRef}
					{...provided.draggableProps}
					className="ChecklistEditor-section"
				>
					<header className="ChecklistEditor-section-header">
						<DragHandle dragHandleProps={provided.dragHandleProps} label="Drag section" />
						<Input
							bordered={false}
							className="ChecklistEditor-section-name"
							value={section.name}
							placeholder="Section name"
							aria-label="Section name"
							onChange={e => changeSection({ name: e.target.value })}
						/>
						<SettingChip
							icon={<TeamOutlined />}
							label={who}
							active={section.group_ids.length > 0}
							ariaLabel={`Who sees this section: ${who}`}
							title="Who sees this section?"
							content={
								<WhoSeesIt
									event={event}
									groupIDs={section.group_ids}
									everyoneLabel="Everyone"
									onChange={ids => changeSection({ group_ids: ids })}
								/>
							}
						/>
						<SettingChip
							icon={<ClockCircleOutlined />}
							label={when}
							active={scheduleMode(event, section) !== SCHEDULE.ALWAYS}
							ariaLabel={`Schedule: ${when}`}
							title="When is this section shown?"
							content={<SectionSchedule event={event} section={section} onChange={changeSection} />}
						/>
						<Dropdown
							trigger={['click']}
							menu={{
								items: [{ key: 'delete', label: 'Delete section', danger: true }],
								onClick: confirmDelete,
							}}
						>
							<Button
								type="text"
								size="small"
								icon={<MoreOutlined />}
								aria-label="Section actions"
							/>
						</Dropdown>
					</header>
					<div className="ChecklistEditor-section-description">
						<DescriptionEditor
							label="Section description"
							value={section.description}
							onChange={description => changeSection({ description })}
						/>
					</div>
					<Droppable droppableId={section.id} type={`item-${section.id}`}>
						{dropProvided => (
							<div
								ref={dropProvided.innerRef}
								{...dropProvided.droppableProps}
								className="ChecklistEditor-items"
							>
								{section.items.map((item, itemIndex) => (
									<ChecklistItemRow
										key={item.id}
										sectionID={section.id}
										item={item}
										index={itemIndex}
										event={event}
										groups={groups}
										count={counts ? counts[item.id] || 0 : null}
										saved={savedItemIDs.has(item.id)}
										autoFocus={item.id === focusID}
										onChange={onChangeItem}
										onDelete={onDeleteItem}
										onPickLink={onPickLink}
										onAddAfter={onAddItem}
									/>
								))}
								{dropProvided.placeholder}
							</div>
						)}
					</Droppable>
					<div className="ChecklistEditor-add-item">
						<Button
							type="text"
							size="small"
							icon={<PlusOutlined />}
							onClick={() => onAddItem(section.id)}
						>
							Add item
						</Button>
						<span>or press Enter at the end of an item</span>
					</div>
				</section>
			)}
		</Draggable>
	);
};

export default memo(ChecklistSectionCard);
